import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "../styles/pantallade-login.module.css";
import { hasStudentRole, useAuth } from "../api/AuthContext";
import LoaderOverlay from "../components/LoaderOverlay.jsx";

function PantalladeLogin() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    institution: "",
    campus: "",
  });
  const [errorMsg, setErrorMsg] = useState("");
  const { login, refreshMe } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      setErrorMsg("");
      const instParsed = parseInt(formData.institution, 10);
      const minimalUser = await login(
        formData.email,
        formData.password,
        Number.isFinite(instParsed) ? instParsed : undefined
      );

      // Intentar cargar el perfil completo (incluye rol y primer_acceso)
      const fullUser = await refreshMe();
      const userData = fullUser || minimalUser;
      
      // Verificar si es primer acceso y redirigir a restablecer contraseña
      if (userData && userData.primer_acceso) {
        navigate("/restablecer-contrasena");
      } else if (hasStudentRole(userData)) {
        // Estudiantes van al portal especial (ruta raíz, que usa HomeRedirect)
        navigate("/");
      } else {
        // Resto de usuarios al inicio administrativo
        navigate("/inicio");
      }
    } catch (error) {
      let msg = "Tu correo o tu contraseña son incorrectos";
      if (error && error.message) {
        msg = error.message;
      } else if (error && typeof error === "object") {
        if (error.detail) msg = error.detail;
        else if (error.non_field_errors && Array.isArray(error.non_field_errors)) msg = error.non_field_errors.join(" ");
        else msg = JSON.stringify(error);
      } else if (typeof error === "string") {
        msg = error;
      }
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  const DropdownArrow = () => (
    <svg
      className={styles.dropdownArrow}
      width="8"
      height="6"
      viewBox="0 0 8 6"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M4.32494 5.37181C4.12495 5.63886 3.72448 5.63886 3.5245 5.37181L0.100665 0.799706C-0.146205 0.470042 0.0890312 0 0.500885 0H7.34855C7.7604 0 7.99564 0.470042 7.74877 0.799706L4.32494 5.37181Z"
        fill="#898D8D"
      />
    </svg>
  );

  return (
    <main className={styles.loginScreen} role="main">
      <div className={styles.loginContainer}>
        <LoaderOverlay visible={loading} label="Iniciando sesión..." />
        <header>
          <div className={styles.logoContainer}>
            <img
              src="https://api.builder.io/api/v1/image/assets/TEMP/b2a2315bcd112541ac469d7dc89567dac6bc3d40?width=164"
              alt="Universidad Lux Logo"
              className={styles.logoImage}
            />
          </div>
          <div className={styles.universityTitle}>
            <span className={styles.universityText}>UNIVERSIDAD</span>
            <div className={styles.titleDivider} />
            <span className={styles.luxText}>LUX</span>
          </div>
          <p className={styles.systemSubtitle}>Gestión Universitaria</p>
        </header>

        <form
          className={styles.formContainer}
          onSubmit={handleSubmit}
          autoComplete="on"
        >
          {/* Email */}
          <div>
            <label htmlFor="email" className={styles.passwordLabel}>
              Usuario (Correo Institucional)
            </label>
            <div className={styles.inputWrapper}>
              <div className={styles.inputField}>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="correo@universidad.edu"
                  className={styles.inputText}
                  required
                  aria-describedby="email-help"
                  autoComplete="username"
                />
              </div>
              {errorMsg && (
                <div className={styles.errorTooltip} role="alert" aria-live="polite">
                  <span className={styles.errorIcon} aria-hidden>!</span>
                  {errorMsg}
                </div>
              )}
            </div>
            <div id="email-help" className={styles.visuallyHidden}>
              Ingresa tu correo electrónico institucional
            </div>
          </div>

          {/* Contraseña */}
          <div>
            <label htmlFor="password" className={styles.passwordLabel}>
              Contraseña
            </label>
            <div className={styles.passwordWrapper}>
              <div className={styles.passwordField}>
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={styles.inputText}
                  required
                  aria-describedby="password-help"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className={styles.togglePasswordBtn}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="#898D8D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="1" y1="1" x2="23" y2="23" stroke="#898D8D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#898D8D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="12" r="3" stroke="#898D8D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div id="password-help" className={styles.visuallyHidden}>
              Ingresa tu contraseña
            </div>
          </div>

          {/* Recuperar contraseña */}
          <Link to="/recuperar" className={styles.forgotPassword} aria-label="Recuperar contraseña">
            ¿Olvidaste tu contraseña?
          </Link>

          {/* Botón iniciar sesión */}
          <div className={styles.loginButton}>
            <button type="submit" className={styles.buttonContent} aria-label="Iniciar sesión" disabled={loading}>
              <span className={styles.buttonText}>Iniciar Sesión</span>
            </button>
          </div>
        </form>

        <footer className={styles.copyright}>
          © 2025 Universidad Lux. Todos los derechos reservados.
        </footer>
      </div>
    </main>
  );
}

export default PantalladeLogin;
